import { useState } from "react";

import { useNavigate, useParams } from "react-router-dom";

import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Package,
  RotateCcw,
  Search,
  Trash2,
  Truck,
} from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Progress } from "~/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Skeleton } from "~/components/ui/skeleton";
import { Textarea } from "~/components/ui/textarea";
import { useToast } from "~/hooks/use-toast";
import {
  useCreateInspection,
  useReturnRequest,
  useReturnRequestItems,
  useUpdateReturnRequestStatus,
  useWarehousesByType,
} from "~/hooks/wms";
import { useProfileStore } from "~/store/profileStore";
import type {
  DiscardReasonCode,
  DispositionType,
  ReturnRequestItem,
} from "~/types/returns";
import {
  DISCARD_REASONS_BY_CATEGORY,
  DISCARD_REASON_CATEGORY_LABELS,
  DISCARD_REASON_LABELS,
  DISPOSITION_COLORS,
  DISPOSITION_LABELS,
  DISPOSITION_TO_WAREHOUSE_TYPE,
  RETURN_REASON_LABELS,
} from "~/types/returns";

const dispositionIcons: Record<DispositionType, React.ElementType> = {
  SELLABLE: RotateCcw,
  REPACKAGE: Package,
  DISCARD: Trash2,
  SUPPLIER_RETURN: Truck,
};

export function ReturnInspectionPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { id: userId } = useProfileStore();

  const [selectedItem, setSelectedItem] = useState<ReturnRequestItem | null>(
    null
  );
  const [inspectDialogOpen, setInspectDialogOpen] = useState(false);
  const [disposition, setDisposition] = useState<DispositionType>("SELLABLE");
  const [quantity, setQuantity] = useState<number>(0);
  const [discardReason, setDiscardReason] = useState<DiscardReasonCode | "">(
    ""
  );
  const [notes, setNotes] = useState("");

  const { data: returnRequest, isLoading: requestLoading } =
    useReturnRequest(id);
  const { data: items = [], isLoading: itemsLoading } =
    useReturnRequestItems(id);
  const createInspectionMutation = useCreateInspection();
  const updateStatusMutation = useUpdateReturnRequestStatus();

  // Get warehouse for destination based on disposition
  const warehouseType = DISPOSITION_TO_WAREHOUSE_TYPE[disposition];
  const { data: warehouses = [] } = useWarehousesByType(warehouseType);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<string>("");

  const isLoading = requestLoading || itemsLoading;

  const receivedItems = items.filter((item) => item.status === "received");
  const inspectedItems = items.filter(
    (item) => item.status === "inspected" || item.status === "routed"
  );
  const progress =
    items.length > 0
      ? ((inspectedItems.length + receivedItems.length) / items.length) * 100
      : 0;

  const handleInspectItem = (item: ReturnRequestItem) => {
    setSelectedItem(item);
    setQuantity(item.received_quantity);
    setDisposition("SELLABLE");
    setDiscardReason("");
    setNotes("");
    setSelectedWarehouseId("");
    setInspectDialogOpen(true);
  };

  const confirmInspection = () => {
    if (!selectedItem || !userId) return;
    if (disposition === "DISCARD" && !discardReason) {
      toast({
        title: "Error",
        description: "Debe seleccionar una razón de descarte",
        variant: "destructive",
      });
      return;
    }

    createInspectionMutation.mutate(
      {
        formData: {
          return_request_item_id: selectedItem.id,
          disposition,
          quantity,
          discard_reason:
            disposition === "DISCARD"
              ? (discardReason as DiscardReasonCode)
              : undefined,
          destination_warehouse_id: selectedWarehouseId || undefined,
          notes: notes || undefined,
        },
        userId,
      },
      {
        onSuccess: () => {
          toast({
            title: "Inspección registrada",
            description: `${selectedItem.product?.short_name || "Producto"} clasificado como ${DISPOSITION_LABELS[disposition]}`,
          });
          setInspectDialogOpen(false);
          setSelectedItem(null);

          // Check if all items are inspected
          const remainingReceived = receivedItems.filter(
            (i) => i.id !== selectedItem.id
          ).length;

          if (remainingReceived === 0 && id) {
            // All items inspected, update status to routing
            updateStatusMutation.mutate(
              { id, status: "routing" },
              {
                onSuccess: () => {
                  toast({
                    title: "Inspección completada",
                    description:
                      "Todos los items han sido inspeccionados. Continuando a ubicación.",
                  });
                  navigate(`/wms/returns/${id}/putaway`);
                },
              }
            );
          }
        },
        onError: (error) => {
          toast({
            title: "Error",
            description: `No se pudo registrar la inspección: ${error.message}`,
            variant: "destructive",
          });
        },
      }
    );
  };

  const handleContinueToPutaway = () => {
    if (!id) return;

    updateStatusMutation.mutate(
      { id, status: "routing" },
      {
        onSuccess: () => {
          navigate(`/wms/returns/${id}/putaway`);
        },
      }
    );
  };

  if (isLoading) {
    return (
      <PageContainer>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </PageContainer>
    );
  }

  if (!returnRequest) {
    return (
      <PageContainer>
        <Card>
          <CardContent className="py-8 text-center">
            <AlertTriangle className="mx-auto mb-4 h-12 w-12 text-yellow-500" />
            <p className="text-muted-foreground">Devolución no encontrada</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => navigate("/wms/returns")}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Volver a Devoluciones
            </Button>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Inspección de Devolución"
        description={`${returnRequest.return_number} - ${returnRequest.client?.name || "Sin cliente"}`}
        back={
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 h-9 text-muted-foreground"
            onClick={() => navigate("/wms/returns")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
        }
      />

      <div className="mx-auto w-full max-w-2xl space-y-6">
        {/* Progress Card */}
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  Progreso de inspección
                </span>
                <span className="font-medium">
                  {inspectedItems.length} / {items.length} items
                </span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
          </CardContent>
        </Card>

        {/* Pending Inspection Items */}
        {receivedItems.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Search className="h-4 w-4" />
                Items por Inspeccionar ({receivedItems.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {receivedItems.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between rounded-lg border p-4"
                >
                  <div className="space-y-1">
                    <p className="font-medium">
                      {item.product?.short_name || "Producto"}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {RETURN_REASON_LABELS[item.return_reason]}
                    </p>
                    <Badge variant="secondary">
                      {item.received_quantity} unidades recibidas
                    </Badge>
                  </div>
                  <Button onClick={() => handleInspectItem(item)}>
                    <Search className="mr-2 h-4 w-4" />
                    Inspeccionar
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Inspected Items */}
        {inspectedItems.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                Items Inspeccionados ({inspectedItems.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {inspectedItems.map((item) => {
                // Show disposition from inspections
                const inspectionSummary = item.inspections?.reduce(
                  (acc, insp) => {
                    acc[insp.disposition] =
                      (acc[insp.disposition] || 0) + insp.quantity;
                    return acc;
                  },
                  {} as Record<DispositionType, number>
                );

                return (
                  <div
                    key={item.id}
                    className="rounded-lg border border-green-200 bg-green-50 p-4"
                  >
                    <div className="mb-2 flex items-center justify-between">
                      <p className="font-medium">
                        {item.product?.short_name || "Producto"}
                      </p>
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                    </div>
                    {inspectionSummary &&
                      Object.keys(inspectionSummary).length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {(
                            Object.entries(inspectionSummary) as [
                              DispositionType,
                              number,
                            ][]
                          ).map(([disp, qty]) => {
                            const Icon = dispositionIcons[disp];
                            return (
                              <Badge
                                key={disp}
                                className={DISPOSITION_COLORS[disp]}
                                variant="outline"
                              >
                                <Icon className="mr-1 h-3 w-3" />
                                {DISPOSITION_LABELS[disp]}: {qty}
                              </Badge>
                            );
                          })}
                        </div>
                      )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Continue Button */}
        {inspectedItems.length > 0 && receivedItems.length === 0 && (
          <Card className="border-green-200 bg-green-50">
            <CardContent className="py-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-8 w-8 text-green-500" />
                  <div>
                    <p className="font-medium">Inspección Completada</p>
                    <p className="text-sm text-muted-foreground">
                      Todos los items han sido inspeccionados
                    </p>
                  </div>
                </div>
                <Button onClick={handleContinueToPutaway}>
                  Continuar a Ubicación
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Inspection Dialog */}
        <Dialog open={inspectDialogOpen} onOpenChange={setInspectDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Inspeccionar Producto</DialogTitle>
              <DialogDescription>
                Clasifique el producto según su estado
              </DialogDescription>
            </DialogHeader>
            {selectedItem && (
              <div className="space-y-4 py-4">
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                    Producto
                  </Label>
                  <p className="font-medium">
                    {selectedItem.product?.short_name || "Producto"}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                      Cantidad Recibida
                    </Label>
                    <p className="text-2xl font-bold">
                      {selectedItem.received_quantity}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="quantity">Cantidad a Clasificar</Label>
                    <Input
                      id="quantity"
                      type="number"
                      min={1}
                      max={selectedItem.received_quantity}
                      value={quantity}
                      onChange={(e) => setQuantity(Number(e.target.value))}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Disposición</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {(Object.keys(DISPOSITION_LABELS) as DispositionType[]).map(
                      (disp) => {
                        const Icon = dispositionIcons[disp];
                        const isSelected = disposition === disp;
                        return (
                          <Button
                            key={disp}
                            type="button"
                            variant={isSelected ? "default" : "outline"}
                            className={`justify-start ${isSelected ? "" : DISPOSITION_COLORS[disp]}`}
                            onClick={() => {
                              setDisposition(disp);
                              if (disp !== "DISCARD") setDiscardReason("");
                            }}
                          >
                            <Icon className="mr-2 h-4 w-4" />
                            {DISPOSITION_LABELS[disp]}
                          </Button>
                        );
                      }
                    )}
                  </div>
                </div>

                {disposition === "DISCARD" && (
                  <div className="space-y-2">
                    <Label>Razón de Descarte *</Label>
                    <Select
                      value={discardReason}
                      onValueChange={(value) =>
                        setDiscardReason(value as DiscardReasonCode)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccione una razón" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(DISCARD_REASONS_BY_CATEGORY).map(
                          ([category, reasons]) => (
                            <div key={category}>
                              <div className="px-2 py-1 text-xs font-semibold text-muted-foreground">
                                {
                                  DISCARD_REASON_CATEGORY_LABELS[
                                    category as keyof typeof DISCARD_REASON_CATEGORY_LABELS
                                  ]
                                }
                              </div>
                              {reasons.map((reason) => (
                                <SelectItem key={reason} value={reason}>
                                  {DISCARD_REASON_LABELS[reason]}
                                </SelectItem>
                              ))}
                            </div>
                          )
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {warehouses.length > 0 && (
                  <div className="space-y-2">
                    <Label>Bodega Destino (opcional)</Label>
                    <Select
                      value={selectedWarehouseId}
                      onValueChange={setSelectedWarehouseId}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccione bodega" />
                      </SelectTrigger>
                      <SelectContent>
                        {warehouses.map((wh) => (
                          <SelectItem key={wh.id} value={wh.id}>
                            {wh.code} - {wh.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="notes">Notas (opcional)</Label>
                  <Textarea
                    id="notes"
                    placeholder="Observaciones adicionales..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setInspectDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                onClick={confirmInspection}
                disabled={
                  createInspectionMutation.isPending ||
                  quantity < 1 ||
                  (disposition === "DISCARD" && !discardReason)
                }
              >
                {createInspectionMutation.isPending
                  ? "Guardando..."
                  : "Confirmar Inspección"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </PageContainer>
  );
}

export default ReturnInspectionPage;
